import React, { Component } from "react";
import { connect } from "react-redux";
import actions from "store/actions";
import { showError, showSuccess } from "common/ToastNotifications";
import { NotificationSettingsTable } from "components";

class NotificationSettings extends Component {
    constructor(props) {
        super(props);
        this.state = {
            busy: false,
            busyMessage: "",
            configState: {},
            hasChanges: false,
        };
    }

    componentDidMount() {
        this.setState({
            busy: true,
            busyMessage: "Loading notification settings...",
        });
        this.props.getUserNotificationConfig();
        this.props.getUserNotificationPermission();
    }
    componentDidUpdate(prevProps) {
        this.onGetUserNotificationConfigResultUpdate(prevProps);
        this.onUpdateUserNotificationConfigResultUpdate(prevProps);
    }

    onGetUserNotificationConfigResultUpdate = (prevProps) => {
        const { getUserNotificationConfigResult, notificationConfig } = this.props;
        const { getUserNotificationConfigResult: prevGetResult } = prevProps;

        if (getUserNotificationConfigResult !== prevGetResult && getUserNotificationConfigResult) {
            if (getUserNotificationConfigResult.success) {
                let itemsToProcess = null;

                if (notificationConfig && Array.isArray(notificationConfig.Items)) {
                    itemsToProcess = notificationConfig.Items;
                } else if (Array.isArray(notificationConfig)) {
                    console.warn("NotificationSettings: notificationConfig prop is an array directly. Consider standardizing state structure.");
                    itemsToProcess = notificationConfig;
                }

                if (itemsToProcess) {
                    const mapped = {};
                    itemsToProcess.forEach((item) => {
                        try {
                            if (item && item.enabled && item.eventType) {
                                mapped[item.eventType] = {
                                    name: item.name,
                                    description: item.description,
                                    settings: item.settings ? JSON.parse(JSON.stringify(item.settings)) : {},
                                    categoryKey: item.categoryKey,
                                    categoryDisplayName: item.categoryDisplayName,
                                };
                            } else {
                                console.warn("NotificationSettings: Skipping item due to missing fields or not enabled:", item);
                            }
                        } catch (e) {
                            console.error("NotificationSettings: Error processing item:", item, e);
                        }
                    });
                    if (Object.keys(mapped).length === 0 && itemsToProcess.length > 0) {
                        console.warn(
                            "NotificationSettings: Mapping resulted in an empty configState despite having items to process. Check item structure and 'enabled' status if this is unexpected.",
                        );
                    }

                    this.setState({
                        configState: mapped,
                        hasChanges: false,
                        busy: false,
                        busyMessage: "",
                    });
                } else {
                    console.warn("NotificationSettings: Success reported, but no valid items found to process in notificationConfig.");
                    showError("Notification settings loaded, but data is in an unexpected format.");
                    this.setState({
                        configState: {},
                        hasChanges: false,
                        busy: false,
                        busyMessage: "",
                    });
                }
            } else if (getUserNotificationConfigResult.error) {
                console.error("NotificationSettings: Failed to load notification settings:", getUserNotificationConfigResult.error);
                showError("Failed to load notification settings");
                this.setState({ busy: false, busyMessage: "", configState: {} });
            } else {
                console.warn("NotificationSettings: getUserNotificationConfigResult indicates no success and no explicit error.");
                this.setState({ busy: false, busyMessage: "", configState: {} });
            }
        }
    };

    onUpdateUserNotificationConfigResultUpdate = (prevProps) => {
        const { updateNotificationConfigResult } = this.props;
        const { updateNotificationConfigResult: prevUpdateResult } = prevProps;

        if (updateNotificationConfigResult && updateNotificationConfigResult !== prevUpdateResult) {
            this.setState({ busy: false, busyMessage: "" });
            if (updateNotificationConfigResult.success) {
                showSuccess("Notification Settings Updated");
                this.setState({ hasChanges: false });
            } else if (updateNotificationConfigResult.error) {
                showError("Failed to update notification settings");
            }
        }
    };

    handleToggleChange = (eventType, level, channel) => {
        this.setState((prevState) => {
            if (!prevState.configState || !prevState.configState[eventType] || !prevState.configState[eventType].settings || !prevState.configState[eventType].settings[level]) {
                console.error("Invalid state or eventType/level for toggle:", eventType, level, prevState.configState);
                return prevState;
            }

            const currentSettingValue = prevState.configState[eventType].settings[level][channel];

            return {
                configState: {
                    ...prevState.configState,
                    [eventType]: {
                        ...prevState.configState[eventType],
                        settings: {
                            ...prevState.configState[eventType].settings,
                            [level]: {
                                ...prevState.configState[eventType].settings[level],
                                [channel]: !currentSettingValue,
                            },
                        },
                    },
                },
                hasChanges: true,
            };
        });
    };

    handleUpdate = () => {
        const updatePayload = {};
        if (this.state.configState && typeof this.state.configState === "object") {
            Object.entries(this.state.configState).forEach(([eventType, itemData]) => {
                if (itemData.settings) {
                    updatePayload[eventType] = {
                        settings: JSON.parse(JSON.stringify(itemData.settings)),
                    };
                }
            });
        }

        if (Object.keys(updatePayload).length === 0 && this.state.hasChanges) {
            console.warn("Update initiated with hasChanges=true, but no payload generated. Check configState structure.");
            showError("Could not prepare settings for update.");
            return;
        }
        if (Object.keys(updatePayload).length === 0 && !this.state.hasChanges) {
            return;
        }

        this.setState({
            busy: true,
            busyMessage: "Updating notification settings...",
        });
        this.props.updateUserNotificationConfig(updatePayload);
    };

    render() {
        const { configState, hasChanges, busy, busyMessage } = this.state;
        const { notificationPermission } = this.props;

        return (
            <div className="full-height-flex-container">
                <NotificationSettingsTable
                    configState={configState || {}}
                    hasChanges={hasChanges}
                    onToggleChange={this.handleToggleChange}
                    onUpdate={this.handleUpdate}
                    busy={busy}
                    busyMessage={busyMessage}
                    notificationPermission={notificationPermission}
                />
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        notificationConfig: state.notificationConfig.notificationConfig,
        getUserNotificationConfigResult: state.notificationConfig.getUserNotificationConfigResult,
        updateNotificationConfigResult: state.notificationConfig.updateNotificationConfigResult,
        notificationPermission: state.notificationsPermission.permission,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getUserNotificationConfig: () => dispatch(actions.getUserNotificationConfigRequest()),
        updateUserNotificationConfig: (data) => dispatch(actions.updateUserNotificationConfigRequest(data)),
        getUserNotificationPermission: () => dispatch(actions.getUserNotificationPermissionRequest()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(NotificationSettings);
